import {
  type DropDownNoSelection,
  type DropDownResult,
  type JsonData,
  type JsonDataWrapper,
  type OdinDataRetrievalOptions,
  OdinDataSender,
} from '@myosh/odin-components';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Subject } from 'rxjs';
import type { RecordResult } from '../@types/records';
import MyoshLogo from '../assets/myosh-logo.svg';
import OdinDropDownComponent from '../components/fields/custom-drop-downs/odin-drop-down.component';
import { RecordSaveState } from '../components/record/record.component';
import { BACKGROUND_SAVE_KEY, mutateRecord } from '../components/record/util/record-mutate';
import useIsMetadataKey from '../hooks/use-is-metadata-key';
import { useGetWorkplaceFormsQuery } from '../redux/services/api';
import { useLazyGetRecordsQuery } from '../redux/services/record';
import { promiseToast } from '../services/notification.service';
import { forceAssert } from '../utils/type-guards';

function WorkplaceSignOutPage() {
  const [selectedWorkplaceSignInRecord, setSelectedWorkplaceSignInRecord] = useState<RecordResult>();
  const destroySubject = useRef(new Subject<void>());
  const isBackgroundSaveEnabled = useIsMetadataKey(BACKGROUND_SAVE_KEY, true);
  const [getRecords] = useLazyGetRecordsQuery();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data } = useGetWorkplaceFormsQuery();
  const workplaceForm = data?.[0];

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const logOutStep = useMemo(() => {
    const signOutButton = workplaceForm?.workflowSteps?.find(
      (item) => item.id === workplaceForm?.signOutWorkflowStepId
    );
    return signOutButton?.label as unknown as string;
  }, [workplaceForm]);

  const getOptionsData = useMemo(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options && workplaceForm?.id && workplaceForm?.fullNameFieldId && workplaceForm?.signInWorkflowStepId) {
          getRecords(
            {
              keyTypeId: true,
              formId: workplaceForm.id as number,
              page: options?.page ?? 1,
              pageSize: 100,
              filters: {
                status: {
                  value: [workplaceForm.signInWorkflowStepId],
                  comparison: 'contains',
                  customDataProperties: {
                    entityType: 'KEYWORD',
                    entityName: 'Status',
                  },
                },
              },
              globalHierarchyFilters: '',
              sortDirective: '',
            },
            true
          )
            .unwrap()
            .then((result) => {
              if (result.length > 0) {
                const dataItems = result
                  .map((item) => {
                    return {
                      ...item.flat,
                      text: (item.original.fields[Number(workplaceForm.fullNameFieldId)] ?? '') as string,
                      value: item.original.id,
                    };
                  })
                  .sort((a, b) => (a.text ?? '').localeCompare(b.text ?? ''));

                subscriber.sendData({
                  data: forceAssert<JsonData>(dataItems),
                  requestId: options?.requestId,
                });
              } else {
                subscriber.sendData();
              }
            })
            .catch(() => subscriber.sendData());
        }
      },
    };
  }, [workplaceForm?.id, workplaceForm?.fullNameFieldId, workplaceForm?.signInWorkflowStepId]);

  const onSubmit = useCallback(() => {
    if (selectedWorkplaceSignInRecord && workplaceForm) {
      const saveData = {
        formId: workplaceForm.id,
        id: selectedWorkplaceSignInRecord.id,
        status: logOutStep,
        lastChange: selectedWorkplaceSignInRecord.lastChange,
      };

      promiseToast(
        mutateRecord(saveData as RecordResult, {
          isBackgroundSaveEnabled,
          recordSaveState: RecordSaveState.Update,
          destroySubject: destroySubject.current,
        }).then(() => setTimeout(() => navigate(`/workplace-sign-in`, { replace: true }), 1000)),
        {
          loading: t('performing-save'),
          success: t('sign-out-successful'),
          error: t('sign-out-failed'),
        }
      );
    }
  }, [selectedWorkplaceSignInRecord?.id, workplaceForm?.id, logOutStep, isBackgroundSaveEnabled]);

  const handleSelectionChange = useCallback((value?: DropDownResult | DropDownResult[] | DropDownNoSelection) => {
    if (value && !Array.isArray(value)) {
      setSelectedWorkplaceSignInRecord((value as DropDownResult).originalData as unknown as RecordResult);
    }
  }, []);

  const buttonStyles = clsx(
    'text-[1em] font-semibold rounded shadow-sm py-2 px-16 hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mono-0 border-solid border-2 border-error text-error',
    { invisible: !selectedWorkplaceSignInRecord?.id }
  );

  return (
    <div className="m-auto grid h-full max-w-screen-md place-items-center overflow-y-auto p-4">
      <div className="flex flex-col items-center gap-y-12 rounded-2xl border border-gray-4 p-8 shadow-xl">
        <OdinDropDownComponent
          textField="text"
          valueField="value"
          data={getOptionsData}
          onChange={handleSelectionChange}
          label={t('sign-out-workplace-dropdown-label')}
          minimumSearchCharacters={2}
          showDataOnSearch={true}
        />

        <button className={buttonStyles} onClick={onSubmit}>
          {t('sign-out')}
        </button>

        <img src={MyoshLogo} alt="myosh-logo" className="aspect-video h-32 w-64" />
      </div>
    </div>
  );
}

export default React.memo(WorkplaceSignOutPage);
